'use client';

import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/modal';
import { Input } from '@heroui/input';
import { Spacer } from '@heroui/spacer';
import { Button } from '@heroui/button';
import { isBlank } from '@/lib/utils';
import Image from 'next/image';
import { FaTelegram } from 'react-icons/fa';
import { getCookie, setCookie } from 'cookies-next';
import { useCallback, useEffect, useState } from 'react';
import { PLAUSIBLE_EVENTS, trackPlausibleEvent } from '@/lib/client-stats';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { RouteNames } from '@/lib/constants-ui';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

interface Translations {
    signupTitle?: string;
    loginLinkText?: string;
    tryWithoutRegistrationText?: string;
    joinVia?: string;
    joinOptions?: {
        email: string;
        telegram: string;
    };
    teaser?: {
        p1?: string;
        p2?: string;
        p3?: string;
    };
    telegramAuthForm?: {
        inputPlaceholder?: string;
        sendCodeButton?: string;
    };
    emailAuthForm?: {
        emailInputLabel?: string;
        emailInputPlaceholder?: string;
        codeInputPlaceholder?: string;
        sendCodeButton?: string;
        checkEmail?: string;
    };
    shared?: {
        title?: string;
        welcome?: string;
        requestCodeButton?: string;
        requestCodeInfo?: string;
        codeInputLabel?: string;
        verifyCodeButton?: string;
        verifying?: string;
        codeInputPlaceholder?: string;
        codeInputError?: string;
        codeInputSuccess?: string;
        cancel?: string;
        sendCodeButton?: string;
        sendingCode?: string;
        codeExpiresIn?: string;
        resendCode?: string;
    };
    buttons?: {
        telegramAuthSubmitButton: string;
        emailAuthSubmit: string;
        telegramRegisterButton: string;
        emailRegisterButton: string;
    };
    errors?: {
        failedToSendCode: string;
    };
};

function getTranslations(nativeLanguage: string): Translations {
    if (nativeLanguage === "english") {
        return {
            signupTitle: 'Join the fun – it\'s free!',
            loginLinkText: 'Existing users login here',
            tryWithoutRegistrationText: 'Try it without registration',
            joinVia: "Join via:",
            joinOptions: {
                email: "Email",
                telegram: "Telegram",
            },
            teaser: {
                p1: "✨ Hundreds of carefully crafted articles and speech comprehension tutorials, quizzes, word games await!",
                p2: "💬 Unique NPCs to talk to, experts in topics: immigration officers, banking clerks, job interviewers and more!",
                p3: "🔥 Specially crafted for busy grown-ups, immigrants and digital nomads in mind! 5 minutes per day is all it takes!"
            },
            telegramAuthForm: {
                inputPlaceholder: 'Code from Telegram',
                sendCodeButton: 'Open Telegram to continue',
            },
            emailAuthForm: {
                emailInputLabel: 'Enter your email address',
                emailInputPlaceholder: 'Email address',
                codeInputPlaceholder: 'Code from email',
                sendCodeButton: 'Request code to continue',
                checkEmail: 'Check your email for the authentication code',
            },
            shared: {
                title: 'Login',
                codeInputLabel: 'Authentication Code',
                verifyCodeButton: 'Verify Code',
                verifying: 'Verifying...',
                codeInputPlaceholder: 'Code from Telegram',
                codeInputError: 'Invalid code',
                codeInputSuccess: 'Code verified successfully',
                cancel: 'Cancel',
                sendCodeButton: 'Send Code',
                sendingCode: 'Sending...',
                codeExpiresIn: 'Code expires in',
                resendCode: 'Resend Code',
            },
            buttons: {
                telegramAuthSubmitButton: 'Verify & Enter',
                emailAuthSubmit: 'Verify & Enter',
                telegramRegisterButton: 'Continue',
                emailRegisterButton: 'Continue',
            },
            errors: {
                failedToSendCode: 'Failed to send authentication code. Please try again.',
            }
        }
    } else if (nativeLanguage === "russian") {
        return {
            signupTitle: 'Присоединяйтесь – это бесплатно!',
            loginLinkText: 'Уже зарегистрированы? Вход здесь!',
            tryWithoutRegistrationText: 'Попробовать без регистрации',
            joinVia: "Регистрация через:",
            joinOptions: {
                email: "Email",
                telegram: "Telegram",
            },
            teaser: {
                p1: "✨ Сотни тщательно созданных статей и руководств по пониманию речи, викторины и языковые ролевые игры ждут вас!",
                p2: "💬 Уникальные NPC для общения, эксперты в темах: иммиграционные чиновники, банковские сотрудники, собеседования на работу и многое другое!",
                p3: "🔥 Специально создан для занятых взрослых – международных профессионалов, иммигрантов и цифровых кочевников! Вам нужно только 5 минут в день!"
            },
            telegramAuthForm: {
                inputPlaceholder: 'Код из Telegram',
                sendCodeButton: 'Открыть Telegram для продолжения',
            },
            emailAuthForm: {
                emailInputLabel: 'Введите ваш email адрес',
                emailInputPlaceholder: 'Email адрес',
                codeInputPlaceholder: 'Код из email',
                sendCodeButton: 'Получить код входа',
                checkEmail: 'Проверьте ваш email на наличие кода для авторизации',
            },
            shared: {
                title: 'Вход',
                codeInputLabel: 'Код авторизации',
                verifyCodeButton: 'Проверить код',
                verifying: 'Проверяется...',
                codeInputPlaceholder: 'Код из Telegram',
                codeInputError: 'Неверный код',
                codeInputSuccess: 'Код успешно проверен',
                cancel: 'Отмена',
                sendCodeButton: 'Отправить код',
                sendingCode: 'Отправляется...',
                codeExpiresIn: 'Истекает через',
                resendCode: 'Отправить код снова',
            },
            buttons: {
                telegramAuthSubmitButton: 'Проверить и войти',
                emailAuthSubmit: 'Проверить и войти',
                telegramRegisterButton: 'Продолжить',
                emailRegisterButton: 'Продолжить',
            },
            errors: {
                failedToSendCode: 'Не удалось отправить код авторизации. Пожалуйста, попробуйте снова.',
            }
        }
    } else if (nativeLanguage === "spanish") {
        return {
            signupTitle: 'Únete a la diversión – ¡es gratis!',
            loginLinkText: 'Usuarios existentes inician sesión aquí',
            tryWithoutRegistrationText: 'Probar sin registro',
            joinVia: "Únete a través de:",
            joinOptions: {
                email: "Email",
                telegram: "Telegram",
            },
            teaser: {
                p1: "✨ Cientos de artículos y tutoriales de comprensión de voz cuidadosamente creados, cuestionarios, juegos de palabras esperan!",
                p2: "💬 NPCs únicas para conversar, expertos en temas: oficiales de inmigración, cajeros bancarios, entrevistas de trabajo y más!",
                p3: "🔥 Especialmente creado para adultos ocupados, inmigrantes y digitales nomádicos en mente! 5 minutos al día es todo lo que se necesita!",
            },
            telegramAuthForm: {
                inputPlaceholder: "Código de Telegram",
                sendCodeButton: "Abrir Telegram para continuar",
            },
            emailAuthForm: {
                emailInputLabel: "Ingresa tu dirección de email",
                emailInputPlaceholder: "Dirección de email",
                codeInputPlaceholder: "Código de email",
                sendCodeButton: "Solicitar código para continuar",
                checkEmail: "Verifica tu email para el código de autenticación",
            },
            shared: {
                title: "Iniciar sesión",
                codeInputLabel: "Código de autenticación",
                verifyCodeButton: "Verificar código",
                verifying: "Verificando...",
                codeInputPlaceholder: "Código de Telegram",
                codeInputError: "Código inválido",
                codeInputSuccess: "Código verificado correctamente",
                cancel: "Cancelar",
                sendCodeButton: "Enviar código",
                sendingCode: "Enviando...",
                codeExpiresIn: "Código expira en",
                resendCode: "Reenviar código",
            },
            buttons: {
                telegramAuthSubmitButton: "Verificar y entrar",
                emailAuthSubmit: "Verificar y entrar",
                telegramRegisterButton: "Continuar",
                emailRegisterButton: "Continuar",
            },
            errors: {
                failedToSendCode: "Error al enviar el código de autenticación. Por favor, intenta nuevamente.",
            }
        }
    } else {
        return getTranslations('english');
    }
}

interface TelegramAuthFormProps {
    locale: string;
    onClose: () => void;
    authCode: string;
    setAuthCode: (code: string) => void;
    error: string;
    isLoading: boolean;
    isAuthCompleted: boolean;
    onSubmit: (e: React.FormEvent) => void;
}

function TelegramAuthForm({ locale, onClose, authCode, setAuthCode, error, isLoading, isAuthCompleted, onSubmit }: TelegramAuthFormProps) {
    const t = getTranslations(locale) as any;

    return (
        <div>
            <form onSubmit={onSubmit}>

                <div className="mb-4">
                    <Input
                        id="authCode"
                        name="authCode"
                        type="text"
                        required
                        label={t.shared.codeInputLabel}
                        placeholder={t.shared.codeInputPlaceholder}
                        value={authCode}
                        size='md'
                        onChange={(e) => setAuthCode(e.target.value)}
                        autoFocus
                    />
                </div>
                {error && (
                    <div className="text-red-600 text-sm mt-2">{error}</div>
                )}

                <div className="flex items-center justify-center">
                    <Button
                        type="submit"
                        variant="shadow"
                        color="primary"
                        isDisabled={isLoading}
                        isLoading={isLoading}
                    >
                        {isLoading ? t.shared.verifying : t.shared.verifyCodeButton}
                    </Button>
                </div>

            </form>
        </div>
    );
}


interface EmailAuthFormProps {
    locale: string;
    email: string;
    setEmail: (email: string) => void;
    authCode: string;
    setAuthCode: (code: string) => void;
    error: string;
    isLoading: boolean;
    isAuthCompleted: boolean;
    onSendCode: (e: React.FormEvent) => void;
    onVerifyCode: (e: React.FormEvent) => void;
    step: 1 | 2;
    timeRemaining: number;
    onResendCode: () => void;
}

function EmailAuthForm({ locale, email, setEmail, authCode, setAuthCode, error, isLoading, isAuthCompleted, onSendCode, onVerifyCode, step, timeRemaining, onResendCode }: EmailAuthFormProps) {
    const t = getTranslations(locale) as any;

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div>
            {step === 1 ? (
                <form onSubmit={onSendCode}>
                    <div className="mb-4">
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete='email'
                            required
                            label={t.emailAuthForm.emailInputLabel}
                            placeholder={t.emailAuthForm.emailInputPlaceholder}
                            value={email}
                            size='md'
                            onChange={(e) => setEmail(e.target.value)}
                            // autoFocus
                        />
                        {error && (
                            <div className="text-red-600 text-sm mt-2">{error}</div>
                        )}
                    </div>
                    <div className="flex items-center justify-center">
                        <Button
                            type="submit"
                            variant="shadow"
                            color="secondary"
                            disabled={isLoading}
                            isLoading={isLoading}
                            startContent={<EnvelopeIcon className="size-4" />}
                        >
                            {isLoading ? t.shared.sendingCode : t.emailAuthForm.sendCodeButton}
                        </Button>
                    </div>
                </form>
            ) : (
                <form onSubmit={onVerifyCode}>
                    <div>
                        <div className="text-sm text-gray-600 mb-3">
                            {t.emailAuthForm.checkEmail}
                        </div>
                        <Input
                            id="authCode"
                            name="authCode"
                            type="text"
                            required
                            label={t.shared.codeInputLabel}
                            placeholder={t.emailAuthForm.codeInputPlaceholder}
                            value={authCode}
                            size='md'
                            onChange={(e) => setAuthCode(e.target.value)}
                            autoFocus
                        />
                        {error && (
                            <div className="text-red-600 text-sm mt-2">{error}</div>
                        )}
                        <div className="flex items-center justify-center gap-4 mt-3">
                            {timeRemaining > 0 && (<Button
                                type="submit"
                                variant="shadow"
                                color="secondary"
                                disabled={isLoading || timeRemaining === 0 || isAuthCompleted}
                                isLoading={isLoading || isAuthCompleted}
                            >
                                {isLoading ? t.shared.verifying : t.shared.verifyCodeButton}
                            </Button>)}
                            <div className="text-sm text-gray-600">
                                {t.shared.codeExpiresIn}: <span className="font-bold">{formatTime(timeRemaining)}</span>
                            </div>
                            {timeRemaining === 0 && (
                                <Button
                                    size="sm"
                                    variant="light"
                                    color="secondary"
                                    onPress={onResendCode}
                                    disabled={isLoading}
                                >
                                    {t.shared.resendCode}
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    locale: string;
}

// Login modal helps user login via email auth code or Telegram auth code
export default function LoginModal({ isOpen, onClose, locale }: LoginModalProps) {
    const [currentTab, setCurrentTab] = useState<'telegram' | 'email'>('email');

    const [authMode, setAuthMode] = useState<'telegram' | 'email' | null>(null);
    const [authCode, setAuthCode] = useState('');
    const [codeRequested, setCodeRequested] = useState(false);
    const [email, setEmail] = useState('');
    const [emailStep, setEmailStep] = useState<1 | 2>(1);
    const [timeRemaining, setTimeRemaining] = useState(180); // 3 minutes in seconds
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthCompleted, setIsAuthCompleted] = useState(false);

    const t = getTranslations(locale);    

    const handleSendEmailCode = async (e: React.FormEvent) => {
        e.preventDefault();

        trackPlausibleEvent({ name: PLAUSIBLE_EVENTS.seoEmailLoginAttempt });

        try {
            setIsLoading(true);
            setError('');

            const response = await fetch('/api/auth/email/send', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, language: locale }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send code');
            }

            // Move to step 2 and start countdown
            setEmailStep(2);
            setTimeRemaining(180); // Reset to 3 minutes
        } catch (err: any) {
            setError(err.message || 'Failed to send authentication code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendEmailCode = () => {
        setEmailStep(1);
        setAuthCode('');
        setError('');
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError('');

            const response = await fetch('/api/auth/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ authCode, authMode }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Invalid authentication code');
            }

            // Store the token
            setCookie('auth_token', data.token, { maxAge: 5 * 24 * 60 * 60 }); // 5 days
            setIsAuthCompleted(true);

            // Redirect to home page for auth workflow completion
            location.href = '/';
        } catch (err) {
            setError('Invalid or expired authentication code. Please request a new one.');
            setIsAuthCompleted(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTelegramTabPress = () => {
        setCurrentTab('telegram');
        window.open(`https://t.me/${process.env.NEXT_PUBLIC_TELEGRAM_BOT_NAME}?start=login${locale == 'english' ? '' : `_${locale}`}`, '_blank');

        trackPlausibleEvent({ name: PLAUSIBLE_EVENTS.seoTelegramLoginAttempt });
    }

    // Countdown timer for email auth code
    useEffect(() => {
        if (currentTab === 'email' && emailStep === 2 && timeRemaining > 0) {
            const timer = setInterval(() => {
                setTimeRemaining((prev) => Math.max(0, prev - 1));
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [emailStep, timeRemaining, currentTab]);

    // To make sure the signup process is in the same locale as the page, 
    // we need to memorize the locale in the 'locale' cookie, but only for non-authenticated users.
    useEffect(() => {
        // We nee to decide on locale cookie. If there's no auth_token cookie, than it's a guest user 
        // and we should memorize the locale in the 'locale' cookie.    
        const authToken = getCookie('auth_token') as string | null;
    
        if (!authToken) {
            setCookie('locale', locale, { maxAge: 60 * 60 * 24 * 30 * 14 });
        }
    }, []);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" placement="top" classNames={{backdrop: 'bg-white/75 backdrop-blur-sm', base: 'rounded-none shadow-none min-h-full sm:min-h-0 sm:rounded-2xl sm:shadow-lg'}}>
            <ModalContent>
                <ModalHeader>
                    <div className="flex flex-row items-center gap-4">
                        <video
                            className="rounded-full w-12 border-2 border-white shadow-md grow"
                            autoPlay
                            loop
                            muted
                            playsInline
                        >
                            <source src="/images/avatar_chatlanga_talking_1.mp4" type="video/mp4" />
                            <source src="/images/avatar_chatlanga_talking_1.mov" type="video/mov" />
                        </video>
                        <span className="text-lg font-semibold">
                            {t.signupTitle}
                        </span>
                    </div>

                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2 text-sm text-gray-700 mb-2">
                        <p>{t.teaser.p1}</p>
                        <p>{t.teaser.p2}</p>
                        <p>{t.teaser.p3}</p>
                    </div>

                    <div className="flex flex-col gap-0">
                        <div className="text-center mb-0 text-xs text-gray-700">
                            {t.joinVia}
                        </div>

                        <div className="flex flex-row items-center justify-center">
                            <Button
                                variant="light"
                                color={currentTab == 'email' ? 'default' : 'secondary'}
                                size="sm"
                                isDisabled={currentTab == 'email'}
                                className={clsx(currentTab != 'email' && 'underline')}
                                onPress={() => setCurrentTab('email')}
                                startContent={<EnvelopeIcon className="size-4" />}
                            >
                                {t.joinOptions.email}
                            </Button>
                            <Button
                                variant="light"
                                color={currentTab == 'telegram' ? 'default' : 'primary'}
                                size="sm"
                                isDisabled={currentTab == 'telegram'}
                                className={clsx(currentTab != 'telegram' && 'underline')}
                                onPress={handleTelegramTabPress}
                                startContent={<FaTelegram className="size-4" />}
                            >
                                {t.joinOptions.telegram}
                            </Button>
                        </div>
                    </div>

                    {/* Email auth form */}
                    <AnimatePresence mode="popLayout">
                        {currentTab == 'email' && (<motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <EmailAuthForm
                                locale={locale}
                                email={email}
                                setEmail={setEmail}
                                authCode={authCode}
                                setAuthCode={setAuthCode}
                                error={error}
                                isLoading={isLoading}
                                onSendCode={handleSendEmailCode}
                                onVerifyCode={handleVerifyCode}
                                step={emailStep}
                                timeRemaining={timeRemaining}
                                onResendCode={handleResendEmailCode}
                                isAuthCompleted={isAuthCompleted}
                            />
                        </motion.div>)}
                    </AnimatePresence>

                    {/* Telegram auth form */}
                    <AnimatePresence mode="popLayout">
                        {currentTab == 'telegram' && (<motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <TelegramAuthForm
                                locale={locale}
                                onClose={onClose}
                                authCode={authCode}
                                setAuthCode={setAuthCode}
                                error={error}
                                isLoading={isLoading}
                                isAuthCompleted={isAuthCompleted}
                                onSubmit={handleVerifyCode}
                            />
                        </motion.div>)}
                    </AnimatePresence>

                    <div className="text-center text-sm text-gray-700 mt-1">
                        <Button variant="shadow" color="success" as={Link} href={RouteNames.welcomeTryIt} className="">
                            {"🧐 " +t.tryWithoutRegistrationText}
                        </Button>
                    </div>

                    <div className="text-center text-xs text-gray-700 mt-4">
                        <Link href={RouteNames.welcomeLogin} className="text-default-500 underline hover:text-secondary hover:underline">{t.loginLinkText}</Link>
                    </div>
                    <Spacer y={4} />
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}